import {
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import {
  BottomSheet,
  CardWithIntent,
  CbhIcon,
  PageHeaderWrapper,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { LoadingButton } from "../../components/LoadingButton";
import { useInHomeVisitModalsDataContext } from "../../ShiftDiscovery/useInHomeVisitModalsDataContext";
import { BOOKED_VISIT_DETAILS_FULL_PATH } from "../paths";
import { usePendVisit } from "./usePendVisit";

interface PendVisitBottomSheetProps {
  modalState: UseModalState;
}

export function PendVisitBottomSheet(props: PendVisitBottomSheetProps) {
  const { modalState } = props;
  const history = useHistory();
  const { showErrorToast } = useToast();
  const { visit } = useInHomeVisitModalsDataContext();
  const { mutateAsync: pendVisit, isLoading: isPendVisitLoading } = usePendVisit(visit?.id ?? "");
  const [selectedReason, setSelectedReason] = useState<string | undefined>(undefined);

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedReason(event.target.value);
  };

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical" onClose={modalState.closeModal}>
          <LoadingButton
            fullWidth
            variant="contained"
            disabled={!selectedReason}
            isLoading={isPendVisitLoading}
            onClick={async () => {
              await pendVisit(
                {
                  data: {
                    attributes: { pendingVisitReason: selectedReason ?? "" },
                    type: "visit",
                  },
                },
                {
                  onError: () => {
                    showErrorToast("Unable to pend visit. Please try again.");
                  },
                }
              );
              history.replace(
                generatePath(BOOKED_VISIT_DETAILS_FULL_PATH, {
                  caseId: visit?.attributes.caseId ?? "",
                  visitId: visit?.id ?? "",
                })
              );
            }}
          >
            Pend visit
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent>
        <PageHeaderWrapper variant="tertiary" />
        <Stack spacing={4}>
          <Title semibold component="h3" variant="h3" sx={{ flex: 1, textAlign: "center" }}>
            Pend your visit
          </Title>

          <Text variant="body1" sx={{ textAlign: "center" }}>
            Please provide a reason for pending the visit.
          </Text>

          <FormControl component="fieldset">
            <RadioGroup
              aria-label="pend-reason"
              name="pend-reason"
              value={selectedReason}
              onChange={handleReasonChange}
            >
              <FormControlLabel
                value="noEmrAccess"
                control={<Radio sx={{ display: "none" }} />}
                slotProps={{
                  typography: { width: "100%", padding: 2 },
                }}
                label={
                  <CardWithIntent
                    intent={selectedReason === "noEmrAccess" ? "neutral" : "disabled"}
                    onClick={() => {
                      setSelectedReason("noEmrAccess");
                    }}
                  >
                    <Stack direction="row" padding={6} spacing={5} alignItems="top">
                      <CbhIcon type="remove" size="medium" />
                      <Stack>
                        <Text variant="h6">No access</Text>
                        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                          The agency hasn&apos;t provided EMR access
                        </Text>
                      </Stack>
                    </Stack>
                  </CardWithIntent>
                }
              />
              <FormControlLabel
                value="patientUnresponsive"
                control={<Radio sx={{ display: "none" }} />}
                slotProps={{
                  typography: { width: "100%", padding: 2 },
                }}
                label={
                  <CardWithIntent
                    intent={selectedReason === "patientUnresponsive" ? "neutral" : "disabled"}
                    onClick={() => {
                      setSelectedReason("patientUnresponsive");
                    }}
                  >
                    <Stack direction="row" padding={6} spacing={5} alignItems="top">
                      <CbhIcon type="user" size="medium" />
                      <Stack>
                        <Text variant="h6">Patient is unavailable</Text>
                        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                          Can&apos;t get a hold of patient to confirm date
                        </Text>
                      </Stack>
                    </Stack>
                  </CardWithIntent>
                }
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
